import {
  Button,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useClipboard,
  VStack,
} from "@chakra-ui/react";
import { createDialogContext, DialogProvider } from "../../Common/Dialog";
import { useContext, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { KYDOrganizationEventLink } from "@common/interfaces/KYDOrganization";
import PermissionSelector from "../EventDetail/Nav/Settings/Permissions";
import { KYDOrganizationUser } from "@common/interfaces/KYDUser";
import PhoneInput from "react-phone-input-2";
import { KYDScope } from "@common/interfaces/ICognito";

export const CreateTeamMemberDialogContext = createDialogContext();

const CreateTeamMember = ({
  editingTeamMember,
  isLoading = false,
  onDelete,
  onInvite,
  onUpdate,
  onClose,
}: {
  isLoading?: boolean;
  editingTeamMember?: KYDOrganizationUser;
  onInvite?: (values: Partial<KYDOrganizationUser>) => Promise<void>;
  onUpdate?: (userId: string, scopes: KYDScope[]) => Promise<void>;
  onDelete: (item: KYDOrganizationUser) => Promise<void>;
  onClose: () => void;
}) => {
  const { isOpen, close } = useContext(CreateTeamMemberDialogContext);

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    reset,
    control,
    formState: { isValid, isDirty },
  } = useForm({
    mode: "all",
    criteriaMode: "all",
    values: {
      ...editingTeamMember,
      scopes: editingTeamMember ? editingTeamMember.scopes : [],
    },
  });

  const scopes = watch("scopes");
  const _onSubmit = async (values: Partial<KYDOrganizationEventLink>) => {
    try {
      if (editingTeamMember) {
        await onUpdate(editingTeamMember.id, values.scopes);
      } else {
        await onInvite(values);
      }
    } catch (e) {}
  };

  const _onClose = () => {
    reset();
    close();
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      size="xxl"
      isCentered
      onClose={() => {
        _onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader paddingTop={4}>
          <Text w="full">
            {editingTeamMember
              ? `Edit ${editingTeamMember.first_name}`
              : "Invite New Team Member"}
          </Text>
        </ModalHeader>
        <ModalBody>
          <VStack w="full" spacing={2}>
            <VStack mb={4} w="full">
              {!editingTeamMember ? (
                <Stack spacing={4} w="100%">
                  <HStack>
                    <Stack spacing={0} w="50%">
                      <Text fontWeight={"medium"}>First Name</Text>
                      <Input
                        {...register("first_name", {
                          required: true,
                        })}
                        isDisabled={!!editingTeamMember}
                        placeholder="First Name"
                      />
                    </Stack>
                    <Stack spacing={0} w="50%">
                      <Text fontWeight={"medium"}>First Name</Text>
                      <Input
                        {...register("last_name", {
                          required: true,
                        })}
                        isDisabled={!!editingTeamMember}
                        placeholder="Last Name"
                      />
                    </Stack>
                  </HStack>
                  <Stack spacing={0} w="100%">
                    <Text fontWeight={"medium"}>Email</Text>
                    <Input
                      {...register("email", {
                        required: true,
                      })}
                      isDisabled={!!editingTeamMember}
                      placeholder="Email"
                    />
                  </Stack>
                  <Stack spacing={0} w="100%">
                    <Text fontWeight={"medium"}>Phone Number</Text>
                    <HStack>
                      <Controller
                        name="phone_number"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value } }) => (
                          <PhoneInput
                            value={value}
                            onChange={onChange}
                            autoFormat={true}
                            inputProps={{
                              placeholder: "Mobile number",
                            }}
                            inputStyle={{
                              width: "100%",
                              height: "60px",
                              fontFamily: "Work sans",
                              borderWidth: "1px",
                              borderRadius: "5px",
                            }}
                            countryCodeEditable={false}
                            country={"us"}
                          />
                        )}
                      />
                    </HStack>
                  </Stack>
                </Stack>
              ) : null}
            </VStack>
            <PermissionSelector
              mode="team"
              scopes={scopes}
              onChange={(values) =>
                setValue("scopes", values.scopes, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button mr="4" isDisabled={isLoading} onClick={_onClose}>
            Close
          </Button>
          {editingTeamMember ? (
            <HStack>
              <Button
                bg="red.500"
                color="white"
                isDisabled={isLoading}
                isLoading={isLoading}
                onClick={() => {
                  onDelete(editingTeamMember);
                  _onClose();
                }}
              >
                Delete
              </Button>
              <Button
                bg="green.500"
                color="white"
                isDisabled={isLoading || !isDirty}
                isLoading={isLoading}
                onClick={() => _onSubmit({ scopes })}
              >
                Save
              </Button>
            </HStack>
          ) : (
            <Button
              bg="green.500"
              color="white"
              isDisabled={isLoading || !isValid}
              isLoading={isLoading}
              onClick={handleSubmit(_onSubmit)}
            >
              Invite
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const withCreateTeamMemberDialog =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <DialogProvider context={CreateTeamMemberDialogContext}>
        <Component {...(props as T)} />
      </DialogProvider>
    );
  };

export default CreateTeamMember;
